import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Shield,
  AlertTriangle,
  FileText,
  Eye,
  Loader2,
  Plus,
  Search,
  BarChart3,
  Scale,
  ShieldAlert,
  Award,
  Gavel,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DashboardStats {
  active_policies: number;
  open_incidents: number;
  compliance_score: number;
  shadow_ai_tools: number;
}

interface RecentIncident {
  id: string;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: string;
  created_at: string;
}

interface DashboardResponse {
  stats: DashboardStats;
  recent_incidents: RecentIncident[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function severityBadge(severity: string) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (severity) {
    case 'critical':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Critical</span>;
    case 'high':
      return <span className={`${base} bg-orange-500/15 text-orange-500`}>High</span>;
    case 'medium':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Medium</span>;
    case 'low':
      return <span className={`${base} bg-blue-500/15 text-blue-500`}>Low</span>;
    default:
      return <span className={`${base} bg-muted text-muted-foreground`}>{severity}</span>;
  }
}

function statusBadge(status: string) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (status) {
    case 'open':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Open</span>;
    case 'investigating':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Investigating</span>;
    case 'resolved':
      return <span className={`${base} bg-green-500/15 text-green-500`}>Resolved</span>;
    case 'closed':
      return <span className={`${base} bg-muted text-muted-foreground`}>Closed</span>;
    default:
      return <span className={`${base} bg-muted text-muted-foreground`}>{status}</span>;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-500';
  if (score >= 50) return 'text-yellow-500';
  return 'text-red-500';
}

// ---------------------------------------------------------------------------
// Navigation cards config
// ---------------------------------------------------------------------------

const NAV_CARDS = [
  { label: 'Policies', description: 'Manage AI usage policies and enforcement rules', icon: FileText, href: '/governance/policies' },
  { label: 'Risk Dashboard', description: 'View risk assessments across all AI systems', icon: BarChart3, href: '/governance/risk' },
  { label: 'Compliance', description: 'Track compliance frameworks and requirements', icon: Scale, href: '/governance/compliance' },
  { label: 'Incidents', description: 'Manage AI-related incidents and resolutions', icon: AlertTriangle, href: '/governance/incidents' },
  { label: 'Shadow AI', description: 'Detect and govern unsanctioned AI tool usage', icon: Eye, href: '/governance/shadow-ai' },
  { label: 'Certifications', description: 'Track AI certifications and renewal dates', icon: Award, href: '/governance/certifications' },
  { label: 'Legal Advisory', description: 'Submit and track legal AI advisory requests', icon: Gavel, href: '/governance/legal' },
  { label: 'Scanner', description: 'Run compliance scans against EU AI Act, NIST, ISO', icon: ShieldAlert, href: '/governance/scanner' },
];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function GovernanceDashboard() {
  const dashboardQuery = useQuery<DashboardResponse>({
    queryKey: ['governance', 'dashboard'],
    queryFn: () => api<DashboardResponse>('governance/policies', { query: { dashboard: 'true' } }),
  });

  const stats = dashboardQuery.data?.stats;
  const recentIncidents = dashboardQuery.data?.recent_incidents ?? [];

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Shield className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">AI Governance</h1>
          <p className="text-muted-foreground mt-1">
            Centralized governance hub for AI policies, compliance, risk management, and incident tracking.
          </p>
        </div>
      </div>

      {/* Stats cards */}
      {dashboardQuery.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Active Policies</p>
            <p className="text-2xl font-bold text-foreground">{stats?.active_policies ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Open Incidents</p>
            <p className="text-2xl font-bold text-foreground">{stats?.open_incidents ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Compliance Score</p>
            <p className={`text-2xl font-bold ${scoreColor(stats?.compliance_score ?? 0)}`}>
              {stats?.compliance_score ?? 0}%
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Shadow AI Tools</p>
            <p className="text-2xl font-bold text-foreground">{stats?.shadow_ai_tools ?? 0}</p>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="flex flex-wrap gap-3">
        <a
          href="/governance/policies"
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Policy
        </a>
        <a
          href="/governance/scanner"
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
        >
          <Search className="h-4 w-4" />
          Run Scan
        </a>
        <a
          href="/governance/compliance"
          className="px-4 py-2 rounded-md text-sm font-medium border border-border text-foreground hover:bg-accent transition-colors flex items-center gap-2"
        >
          <Scale className="h-4 w-4" />
          View Compliance
        </a>
        <a
          href="/governance/shadow-ai"
          className="px-4 py-2 rounded-md text-sm font-medium border border-border text-foreground hover:bg-accent transition-colors flex items-center gap-2"
        >
          <Eye className="h-4 w-4" />
          Shadow AI
        </a>
      </div>

      {/* Recent Incidents */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">Recent Incidents</h2>
        {dashboardQuery.isLoading ? (
          <div className="rounded-lg border border-border bg-card p-12 flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading...</span>
          </div>
        ) : recentIncidents.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
            No recent incidents. Your AI systems are running smoothly.
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="flex items-center gap-4 px-4 py-2 bg-muted/50 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wider">
              <span className="flex-1">Title</span>
              <span className="w-20">Severity</span>
              <span className="w-24">Status</span>
              <span className="w-32">Reported</span>
            </div>
            {recentIncidents.slice(0, 5).map((incident) => (
              <div
                key={incident.id}
                className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0 hover:bg-accent/50"
              >
                <span className="flex-1 text-sm text-foreground truncate">{incident.title}</span>
                <span className="w-20">{severityBadge(incident.severity)}</span>
                <span className="w-24">{statusBadge(incident.status)}</span>
                <span className="w-32 text-sm text-muted-foreground">{formatDate(incident.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Navigation cards */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">Governance Modules</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {NAV_CARDS.map((card) => (
            <a
              key={card.href}
              href={card.href}
              className="rounded-lg border border-border bg-card p-5 hover:border-primary/50 transition-colors group"
            >
              <card.icon className="h-5 w-5 text-primary mb-3" />
              <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">
                {card.label}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">{card.description}</p>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
